import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import { getLinkedProgrammeModuleReadiness } from "../../../../services/ProgramService";
import {
    getCampusText,
    getMethodIcon,
    getMethodText,
    getReadiness,
    isVersionActive,
} from "../../../../utils/LinkedProgrammeUtils";
import { StyledCampusBadge } from "./linkedProgrammes.styles";
import ModuleReadinessIssuesList from "./ModuleReadinessIssuesList";
import ModuleReadinessReadyModules from "./ModuleReadinessReadyModules";
import {
    StyledErrorSummary,
    StyledModulesPopover,
    StyledOpenModulesLink,
    StyledOverrideNotice,
    StyledPopoverClose,
    StyledPopoverContent,
    StyledPopoverDivider,
    StyledPopoverFooter,
    StyledPopoverFooterSpacer,
    StyledPopoverHeader,
    StyledPopoverHeaderTop,
    StyledPopoverMeta,
    StyledPopoverMethod,
    StyledPopoverProgramme,
    StyledPopoverProgrammeName,
    StyledPopoverSmallText,
    StyledPopoverVersion,
} from "./ModuleReadinessPopover.styles";

/**
 * Positions the popover relative to its trigger button, clamped inside the viewport.
 * @param {HTMLElement} button The trigger button element to anchor to.
 * @param {boolean} [expanded=false] Whether the expanded (wider/taller) layout is shown.
 * @returns {{top: number, left: number}}
 */
function getPopoverPosition(
    button,
    expanded = false
) {
    if (!button) {
        return {
            top: 12,
            left: 12,
        };
    }

    const rect = button.getBoundingClientRect();

    const width = expanded ? 361 : 416;
    const estimatedHeight = expanded ? 500 : 297;

    const viewportPadding = 12;
    const gap = 6;

    let left = rect.right - width;

    left = Math.max(
        viewportPadding,
        Math.min(
            left,
            window.innerWidth -
                width -
                viewportPadding
        )
    );

    let top = rect.bottom + gap;

    if (
        top +
            estimatedHeight +
            viewportPadding >
        window.innerHeight
    ) {
        top =
            rect.top -
            estimatedHeight -
            gap;
    }

    top = Math.max(
        viewportPadding,
        top
    );

    return {
        top,
        left,
    };
}

/**
 * Popover showing a linked programme's module-readiness problems and ready modules.
 * @param {object} row The linked-programme row to show readiness for.
 * @param {string|number} pmcId The programme's id.
 * @param {HTMLElement} anchorElement Trigger element the popover is positioned against.
 * @param {Function} onClose Closes the popover.
 */
function ModuleReadinessPopover({
    row,
    pmcId,
    anchorElement,
    onClose,
}) {
    const modulesPopoverRef = useRef(null);

    const [
        showReadyModules,
        setShowReadyModules,
    ] = useState(false);

    const [
        modulesPopoverPosition,
        setModulesPopoverPosition,
    ] = useState({
        top: 0,
        left: 0,
    });

    const [freshRow, setFreshRow] = useState(null);

    useEffect(() => {
        setFreshRow(null);

        if (!row?.id || !pmcId) {
            return undefined;
        }

        const source = axios.CancelToken.source();

        const loadModuleReadiness = async () => {
            try {
                const response = await getLinkedProgrammeModuleReadiness(
                    pmcId,
                    row.id,
                    source.token
                );
                setFreshRow(response?.data?.data || null);
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.log(
                        "ModuleReadinessPopover.js->loadModuleReadiness->Error",
                        error
                    );
                }
            }
        };

        loadModuleReadiness();

        return () => {
            source.cancel("New request made");
        };
    }, [row?.id, pmcId]);

    const readiness = useMemo(
        () =>
            row
                ? getReadiness(freshRow || row)
                : null,
        [row, freshRow]
    );

    useEffect(() => {
        if (
            !row ||
            !anchorElement
        ) {
            return undefined;
        }

        const updatePosition = () => {
            setModulesPopoverPosition(
                getPopoverPosition(
                    anchorElement,
                    showReadyModules
                )
            );
        };

        updatePosition();

        window.addEventListener(
            "resize",
            updatePosition
        );

        window.addEventListener(
            "scroll",
            updatePosition,
            true
        );

        return () => {
            window.removeEventListener(
                "resize",
                updatePosition
            );

            window.removeEventListener(
                "scroll",
                updatePosition,
                true
            );
        };
    }, [
        row,
        anchorElement,
        showReadyModules,
    ]);

    useEffect(() => {
        if (!row) {
            return undefined;
        }

        const handlePointerDown = (
            event
        ) => {
            const popover =
                modulesPopoverRef.current;

            if (
                popover?.contains(
                    event.target
                ) ||
                anchorElement?.contains(
                    event.target
                )
            ) {
                return;
            }

            onClose();
        };

        const handleKeyDown = (
            event
        ) => {
            if (
                event.key === "Escape"
            ) {
                onClose();
            }
        };

        document.addEventListener(
            "mousedown",
            handlePointerDown
        );

        document.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handlePointerDown
            );

            document.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };
    }, [
        row,
        anchorElement,
        onClose,
    ]);

    // Reset the expanded "Ready Modules" view whenever the popover closes OR is
    // redirected straight to a different row, not just on close.
    useEffect(() => {
        setShowReadyModules(false);
    }, [row?.id]);

    if (
        !row ||
        !readiness
    ) {
        return null;
    }

    return (
        <StyledModulesPopover
            ref={modulesPopoverRef}
            $expanded={
                showReadyModules
            }
            $top={
                modulesPopoverPosition.top
            }
            $left={
                modulesPopoverPosition.left
            }
            role="dialog"
            aria-label={`${row?.name || "Programme"} module readiness`}
        >
            <StyledPopoverHeader>
                <StyledPopoverHeaderTop>
                    <StyledPopoverProgramme>
                        <StyledPopoverProgrammeName $maxWidth="320px">
                            {row?.name ||
                                "Programme"}
                        </StyledPopoverProgrammeName>

                        <StyledPopoverMeta>
                            <StyledCampusBadge
                                $campus={getCampusText(
                                    row?.campus
                                )}
                            >
                                {getCampusText(
                                    row?.campus
                                )}
                            </StyledCampusBadge>

                            <StyledPopoverMethod>
                                <i
                                    className={`fal fa-${getMethodIcon(
                                        getMethodText(
                                            row?.type
                                        )
                                    )}`}
                                    aria-hidden="true"
                                />

                                {getMethodText(
                                    row?.type
                                )}
                            </StyledPopoverMethod>

                            <StyledPopoverDivider />

                            <StyledPopoverSmallText>
                                {row?.intakes ||
                                    "-"}
                            </StyledPopoverSmallText>

                            <StyledPopoverVersion $active={isVersionActive(row)}>
                                <i
                                    className={`fas ${
                                        isVersionActive(
                                            row
                                        )
                                            ? "fa-check-circle"
                                            : "fa-times-circle"
                                    }`}
                                    aria-hidden="true"
                                />

                                {row?.version_name ||
                                    "-"}
                            </StyledPopoverVersion>
                        </StyledPopoverMeta>
                    </StyledPopoverProgramme>

                    <StyledPopoverClose
                        type="button"
                        onClick={onClose}
                        aria-label="Close module readiness"
                    >
                        <i
                            className="fal fa-times"
                            aria-hidden="true"
                        />
                    </StyledPopoverClose>
                </StyledPopoverHeaderTop>

                {Boolean(
                    row?.override_pmc_version
                ) &&
                    String(
                        row?.pmc_version_id
                    ) !==
                        String(
                            row?.version_id
                        ) && (
                        <StyledOverrideNotice>
                            <i
                                className="fas fa-exclamation-triangle"
                                aria-hidden="true"
                            />

                            <span>
                                This programme overrides the active
                                version. Other intakes of this
                                qualification may show different
                                results.
                            </span>
                        </StyledOverrideNotice>
                    )}
            </StyledPopoverHeader>

      <StyledPopoverContent>
        <ModuleReadinessIssuesList problems={readiness.problems} />

        <ModuleReadinessReadyModules
          ready={readiness.ready}
          expanded={showReadyModules}
          onToggle={() => setShowReadyModules((current) => !current)}
        />
      </StyledPopoverContent>

      <StyledPopoverFooter>
        <StyledErrorSummary>
          {
                        readiness.problems
                            .length
                    }{" "}
          of{" "}
          {
                        readiness.total
                    }{" "}
          Modules with Errors
        </StyledErrorSummary>

        <StyledPopoverFooterSpacer />

        <StyledOpenModulesLink
                    to={`/program-management-center/modules/module/table?programmeId=${pmcId}&versionId=${row.active_version_id}`}
                >
          Open Modules tab{" "}

          <i
                        className="fal fa-arrow-right"
                        aria-hidden="true"
                    />
        </StyledOpenModulesLink>
      </StyledPopoverFooter>
    </StyledModulesPopover>
    );
}

export default ModuleReadinessPopover;